import React, { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  DatePicker,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import {
  DATE_TIME_FORMAT,
  DEFAULT_DATES_RANGE_OPTIONS,
} from "../../Utils/constants";
import { numFormatter } from "../../Utils/formatter";
import { getWithdrawalRequests } from "../api";
import BulkWithdrawalHandler from "./BulkWithdrawalHandler";
import WithdrawalHandler from "./WithdrawalHandler";
import WithdrawalOrdersTable from "./WithdrawalOrdersTable";

const STATUS_MAP = {
  pending: {
    text: "Pending",
    color: "warning",
  },
  approved: {
    text: "Approved",
    color: "success",
  },
  rejected: {
    text: "Rejected",
    color: "error",
  },
};

const { Option } = Select;

const razorpayAutomaticPayoutStatuses = [
  "queued",
  "processing",
  "processed",
  "reversed",
  "failed",
];

const WithdrawalsSection = () => {
  const [withdrawalsData, setWithdrawalsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [status, setStatus] = useState("pending");
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  const [presentlyViewing, setPresentlyViewing] = useState("");
  const [transferMode, setTransferMode] = useState("all");
  const [automaticEligible, setAutomaticEligible] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState("all");
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  const [bulkWithdrawalModalMode, setBulkWithdrawalModalMode] = useState("");
  const [bulkWithdrawalModalVisible, setBulkWithdrawalModalVisible] =
    useState(false);
  const [tableKey, setTableKey] = useState(Date.now());

  const getWithdrawalRequestsData = async () => {
    setLoading(true);
    try {
      const resp = await getWithdrawalRequests(
        startDate.toISOString(),
        endDate.toISOString(),
        status,
        search,
        transferMode === "all" ? undefined : transferMode,
        automaticEligible ? true : undefined,
        payoutStatus === "all" ? undefined : payoutStatus,
        page,
        limit
      );
      if (resp?.code === 0) {
        setWithdrawalsData(resp.result?.data);
        setTotal(resp.result?.total);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
    setLoading(false);
  };

  const handleWidthdrawalRequestCSVDownload = async () => {
    try {
      const resp = await getWithdrawalRequests(
        startDate.toISOString(),
        endDate.toISOString(),
        status,
        search,
        transferMode === "all" ? undefined : transferMode,
        automaticEligible ? true : undefined,
        payoutStatus === "all" ? undefined : payoutStatus,
        undefined,
        undefined,
        true
      );
      fileDownload(
        resp,
        `withdrawal-requestscsv_${moment().format("DD-MM-YYYY")}.csv`
      );
      message.success("CSV download started successfully");
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const dataTable = () => {
    const columnData = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <TableUserInfoItem
            name={record?.name}
            profilePic={record?.profilePicUrl}
            email={record?.email}
            phone={record?.phone}
          />
        ),
      },
      {
        title: "Bank Details",
        dataIndex: "accountNumber",
        key: "accountNumber",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            {record?.country === "india" || record?.country === "IN" ? (
              <>
                <Typography>
                  <strong>Account Name: </strong>
                  {record?.accountName}
                </Typography>
                <Typography>
                  <strong>Account Number: </strong>
                  {record?.accountNumber}
                </Typography>
                <Typography>
                  <strong>Bank Name: </strong>
                  {record?.bankName}
                </Typography>
                <Typography>
                  <strong>IFSC: </strong>
                  {record?.ifsc}
                </Typography>
              </>
            ) : (
              <>
                <Typography>
                  <strong>Account Name: </strong>
                  {record?.internationalBankAccount?.account_holder_name}
                </Typography>
                <Typography>
                  <strong>Account Number: </strong>
                  {record?.internationalBankAccount?.account_number}
                </Typography>
                <Typography>
                  <strong>Bank Name: </strong>
                  {record?.internationalBankAccount?.bank_name}
                </Typography>
                <Typography>
                  <strong>Bank ID: </strong>
                  {record?.internationalBankAccount?.bank_id}
                </Typography>
                <Typography>
                  <strong>Currency: </strong>
                  {record?.internationalBankAccount?.currency}
                </Typography>
              </>
            )}
          </Space>
        ),
      },
      {
        title: "PAN",
        dataIndex: "pan",
        key: "pan",
        render: (_, record) =>
          record?.pan ? (
            <Typography>
              <a href={record?.panImage} target="_blank" rel="noreferrer">
                {record?.pan}
              </a>
            </Typography>
          ) : (
            "-"
          ),
      },
      {
        title: "Amount Breakdown",
        dataIndex: "netAmount",
        key: "netAmount",
        render: (_, record) => (
          <Space size="small" direction="vertical" style={{ width: 200 }}>
            <Typography>
              <strong>Currency: </strong>
              {record?.currency}
            </Typography>
            <Typography>
              <strong>Gross Collection: </strong>
              {numFormatter({
                amount: record?.grossCollection,
                currency: record?.currency,
              })}
            </Typography>
            <Typography>
              <strong>Refunds Amount: </strong>
              {numFormatter({
                amount: record?.refunds,
                currency: record?.currency,
              })}
            </Typography>
            <Typography>
              <strong>Testing Amount: </strong>
              {numFormatter({
                amount: record?.testing,
                currency: record?.currency,
              })}
            </Typography>
            <Typography>
              <strong>Net Amount: </strong>
              {numFormatter({
                amount: record?.netAmount,
                currency: record?.currency,
              })}
            </Typography>
            <Typography>
              <strong>Comission Charged: </strong>
              {numFormatter({
                amount: record?.commisionCharged,
                currency: record?.currency,
              })}
            </Typography>
          </Space>
        ),
      },
      {
        title: "Approval Status",
        dataIndex: "approvalStatus",
        key: "approvalStatus",
        render: (approvalStatus, record) => (
          <Space direction="vertical">
            <Tag color={STATUS_MAP[record?.approvalStatus]?.color}>
              {STATUS_MAP[record?.approvalStatus]?.text}
            </Tag>

            {record?.rejectionReason !== undefined &&
              record?.rejectionReason !== null && (
                <Typography>Reason: {record?.rejectionReason}</Typography>
              )}
            {record?.transferMode === "automatic" && (
              <>
                <Typography>Payout status:</Typography>
                <Tag color={STATUS_MAP[record?.payoutStatus]?.color}>
                  {STATUS_MAP[record?.payoutStatus]?.text}
                </Tag>
                <Typography>
                  Payout status description: {record?.payoutStatusDetails}
                </Typography>
              </>
            )}
          </Space>
        ),
      },
      {
        title: "Requested On",
        dataIndex: "recordedWithdrawalDate",
        key: "recordedWithdrawalDate",
        render: (text, record) => (
          <Space direction="vertical" style={{ width: 100 }}>
            <Typography>
              {moment(record?.recordedWithdrawalDate).format(DATE_TIME_FORMAT)}
            </Typography>
            <Typography>
              ({moment(record?.recordedWithdrawalDate).fromNow()})
            </Typography>
          </Space>
        ),
      },
      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        render: (text, record) => (
          <WithdrawalHandler
            withdrawalId={record?._id}
            withdrawalStatus={record?.approvalStatus}
            onWithdrawalStatusChange={(newStatus) =>
              setWithdrawalsData(
                withdrawalsData.reduce(
                  (acc, curr) => [
                    ...acc,
                    curr._id === record?._id
                      ? { ...curr, approvalStatus: newStatus }
                      : curr,
                  ],
                  []
                )
              )
            }
            withdrawalMode={record?.transferMode}
          />
        ),
      },
    ];
    return (
      <Table
        key={tableKey}
        dataSource={loading ? [] : withdrawalsData}
        columns={columnData}
        loading={loading}
        pagination={{
          pageSize: limit,
          total,
          onChange: setPage,
          current: page,
          showSizeChanger: false,
          // onShowSizeChange: (current, size) => setLimit(size),
        }}
        rowKey={(record) => record?._id}
        expandable={{
          expandedRowRender: (record) => (
            <WithdrawalOrdersTable
              withdrawalId={record?._id}
              creatorId={record?.creatorId}
              presentlyViewing={presentlyViewing}
            />
          ),
          expandedRowKeys: [presentlyViewing],
          onExpand: (expanded, record) => {
            if (expanded) {
              setPresentlyViewing(record?._id);
            } else {
              setPresentlyViewing("");
            }
          },
        }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedWithdrawals,
          onChange: (selectedRowKeys) => {
            setSelectedWithdrawals(selectedRowKeys);
          },
          getCheckboxProps: (record) => {
            if (record?.approvalStatus !== "pending") {
              return { disabled: true };
            }
            if (record?.payoutContact?.verificationStatus !== "completed") {
              return { disabled: true };
            }
          },
          preserveSelectedRowKeys: true,
        }}
        locale={{
          emptyText: loading ? "Loading..." : "No withdrawals found",
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  useEffect(() => {
    if (status === "pending") {
      setStartDate(moment("2020-12-28 14:30:14.755Z").startOf("day"));
      setPage(1);
    } else {
      setStartDate(moment().subtract(7, "days").startOf("day"));
      setPage(1);
    }
  }, [status]);

  useEffect(() => {
    getWithdrawalRequestsData();
  }, [
    startDate,
    endDate,
    search,
    automaticEligible,
    transferMode,
    payoutStatus,
    page,
    limit,
  ]);

  return (
    <>
      <FiltersWrapper>
        <CustomSearchbar
          placeholder="Search by name, email, phone or host"
          searchQuery={search}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
          style={{ maxWidth: 500 }}
        />

        <Tooltip title="Refresh">
          <Button size="large" onClick={getWithdrawalRequestsData}>
            <ReloadOutlined />
          </Button>
        </Tooltip>

        <DatePicker.RangePicker
          size="large"
          ranges={DEFAULT_DATES_RANGE_OPTIONS}
          value={startDate && endDate ? [startDate, endDate] : null}
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              const [d1, d2] = dates;
              setStartDate(d1);
              setEndDate(d2);
            } else {
              setStartDate(null);
              setEndDate(null);
            }
          }}
          disabled={status === "pending"}
          style={{ marginLeft: "auto" }}
        />

        <Select
          size="large"
          placeholder="Select approval status"
          onSelect={setStatus}
          value={status}
        >
          <Option value="pending">Pending</Option>
          <Option value="approved">Approved</Option>
          <Option value="rejected">Rejected</Option>
        </Select>

        {status !== "pending" ? (
          <>
            <Select
              size="large"
              placeholder="Select transfer mode"
              value={transferMode}
              onSelect={setTransferMode}
              style={{
                width: 100,
              }}
            >
              <Option value="all">All</Option>
              <Option value="automatic">Automatic</Option>
              <Option value="manual">Manual</Option>
            </Select>
            <Select
              size="large"
              value={payoutStatus}
              onSelect={setPayoutStatus}
              style={{
                width: 100,
              }}
            >
              <Option value="all">All</Option>
              {razorpayAutomaticPayoutStatuses.map((st) => (
                <Option value={st}>{st}</Option>
              ))}
            </Select>
          </>
        ) : (
          <Checkbox
            value={automaticEligible}
            onChange={(e) => setAutomaticEligible(e.target.checked)}
          >
            Automatic Eligible
          </Checkbox>
        )}

        <Button
          className="siteBtn grayBtn"
          onClick={() => handleWidthdrawalRequestCSVDownload()}
        >
          <DownloadOutlined />
          Export CSV
        </Button>
      </FiltersWrapper>

      {selectedWithdrawals?.length > 0 && (
        <Row
          style={{
            marginBottom: "10px",
          }}
        >
          <Space
            style={{
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <Typography>
              {selectedWithdrawals?.length} Withdrawals selected
            </Typography>
            <Button
              type="primary"
              onClick={() => {
                setBulkWithdrawalModalMode("approve");
                setBulkWithdrawalModalVisible(true);
              }}
            >
              Bulk Approve
            </Button>
            <Button
              type="danger"
              onClick={() => {
                setBulkWithdrawalModalMode("reject");
                setBulkWithdrawalModalVisible(true);
              }}
            >
              Bulk Reject
            </Button>
          </Space>
        </Row>
      )}
      <BulkWithdrawalHandler
        withdrawalIds={selectedWithdrawals}
        modalMode={bulkWithdrawalModalMode}
        setModalMode={setBulkWithdrawalModalMode}
        showModal={bulkWithdrawalModalVisible}
        setShowModal={setBulkWithdrawalModalVisible}
        onWithdrawalStatusChange={(withdrawalIds, newStatus) => {
          const withSet = new Set(withdrawalIds);
          setWithdrawalsData(
            withdrawalsData.reduce(
              (acc, curr) => [
                ...acc,
                withSet.has(curr._id)
                  ? { ...curr, approvalStatus: newStatus }
                  : curr,
              ],
              []
            )
          );
        }}
      />
      {dataTable()}
    </>
  );
};

export default WithdrawalsSection;
